import { useEffect, useState, type FormEvent } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { todayISO, formatMoney } from '../lib/format';
import { postPayment } from '../lib/posting';
import type { Account, BankAccount, Bill, Invoice } from '../lib/types';

interface Props {
  direction: 'received' | 'made';
  invoice?: Invoice | null;
  bill?: Bill | null;
  onClose: () => void;
  onPosted: () => void;
}

export default function PaymentModal({ direction, invoice, bill, onClose, onPosted }: Props) {
  const { currentOrg } = useOrg();
  const doc = invoice ?? bill;
  const outstanding = doc ? Math.max(0, Number(doc.total) - Number(doc.amount_paid)) : 0;

  const [banks, setBanks] = useState<BankAccount[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [bankId, setBankId] = useState<string>('');
  const [amount, setAmount] = useState<string>(outstanding.toFixed(2));
  const [date, setDate] = useState<string>(todayISO());
  const [reference, setReference] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!currentOrg) return;
    Promise.all([
      supabase.from('dodobooks_bank_accounts').select('*').eq('org_id', currentOrg.id).eq('is_active', true).order('bank_name'),
      supabase.from('dodobooks_accounts').select('*').eq('org_id', currentOrg.id).eq('is_archived', false),
    ]).then(([b, a]) => {
      setBanks((b.data ?? []) as BankAccount[]);
      setAccounts((a.data ?? []) as Account[]);
      if ((b.data ?? []).length > 0) setBankId((b.data as any[])[0].id);
    });
  }, [currentOrg]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!currentOrg || !doc) return;
    if (!bankId) { setErr('Pick the bank account.'); return; }
    const bank = banks.find((b) => b.id === bankId);
    if (!bank) { setErr('Bank account not found.'); return; }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) { setErr('Enter a positive amount.'); return; }

    setBusy(true); setErr(null);
    const result = await postPayment({
      org_id: currentOrg.id,
      direction,
      contact_id: doc.contact_id,
      invoice: invoice ?? null,
      bill: bill ?? null,
      amount: amt,
      date,
      bank_account: bank,
      accounts,
      reference: reference || null,
      notes: notes || null,
    });
    setBusy(false);
    if (result.error) setErr(result.error);
    else onPosted();
  }

  if (!doc) return null;
  const title = direction === 'received' ? 'Record customer payment' : 'Record supplier payment';
  const verb = direction === 'received' ? 'received from' : 'paid to';

  return (
    <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm grid place-items-center px-4">
      <div className="bg-surface border border-line-strong rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-line">
          <h2 className="h-section">{title}</h2>
          <button onClick={onClose} className="text-text-mute hover:text-text"><X size={18} /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 grid grid-cols-2 gap-4">
          <div className="col-span-2 text-sm text-text-dim bg-ink-2 border border-line rounded-md px-3 py-2">
            Outstanding: <span className="num text-text font-semibold">{formatMoney(outstanding, doc.currency)}</span>
            <span className="text-text-mute"> · {verb} {doc.currency}</span>
          </div>
          <div className="field col-span-2">
            <label>Bank account *</label>
            <select required value={bankId} onChange={(e) => setBankId(e.target.value)}>
              <option value="">— pick a bank account —</option>
              {banks.map((b) => <option key={b.id} value={b.id}>{b.bank_name}{b.account_number ? ` · ${b.account_number}` : ''}</option>)}
            </select>
            {banks.length === 0 && (
              <p className="text-[11px] text-rust mt-1">No bank accounts set up. Add one in Bank accounts first.</p>
            )}
          </div>
          <div className="field"><label>Date *</label><input required type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div className="field"><label>Amount *</label><input required type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
          <div className="field col-span-2"><label>Reference</label><input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="EFT / cheque / receipt number" /></div>
          <div className="field col-span-2"><label>Notes</label><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>

          {err && <div className="col-span-2 text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}

          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
            <button type="submit" disabled={busy || banks.length === 0} className="btn btn-primary disabled:opacity-50">
              <CheckCircle size={14} /> {busy ? 'Posting…' : 'Record payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
